import { CellEditorDialogComponent } from '../cell-editor-dialog/cell-editor-dialog.component';
import { NbDialogService, NbCardModule, NbListModule, NbButtonModule, NbAccordionModule } from '@nebular/theme';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LightgalleryModule } from 'lightgallery/angular';
import { LightGallery } from 'lightgallery/lightgallery';
import lgZoom from 'lightgallery/plugins/zoom';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import * as R from 'ramda';
import { combineLatest, Subscription } from 'rxjs';
import { KPI } from '../../../@core/api';
import { filter, map, switchMap } from 'rxjs/operators';
import { AssetsService, ChartFiltersService, ChartsService, ConfigService, DocumentsService, InitiativesChangesService, InitiativesService, PerimetersService, StateLevelsService, UsersService, UtilsService } from '../../../@core/utils';
import { I18nService } from '../../../@core/utils/i18n.service';
import { SheetDialogComponent } from '../sheet-dialog/sheet-dialog.component';
import { AgGridDocumentsComponent } from '../ag-grid-documents/ag-grid-documents.component';
import { InitiativeFormComponent } from '../side-forms/initiative-form/initiative-form.component';
import { UsernamePipe } from '../../pipes/username.pipe';
import { InitiativeCashFlowChartComponent } from '../charts/initiative-cash-flow-chart/initiative-cash-flow-chart.component';
import { InitiativeTrajectoryChartComponent } from '../charts/initiative-trajectory-chart/initiative-trajectory-chart.component';
import { SiteInitiativeConsumptionsChartComponent } from '../charts/site-initiative-consumptions-chart/site-initiative-consumptions-chart.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgStyle, DatePipe, TitleCasePipe } from '@angular/common';
import { LightGalleryCoreSettings } from 'lightgallery/lg-settings';
import { GROUP_PERMISSION_ACCESS_AUDIT, RequireAtLeastOne } from '../../../@core/api/models';
import { LIGHT_GALLERY_LICENSE_KEY } from '../../../../license-keys';
import { InitiativeGainsListComponent } from '../initiative-gains-list/initiative-gains-list.component';
import { CurrencyPipe } from '../../pipes';
import { InitiativeTypePipe } from '../../pipes/initiative-type.pipe';

@Component({
  selector: 'app-initiative-sheet',
  templateUrl: './initiative-sheet.component.html',
  styleUrls: ['./initiative-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    
    NbCardModule,
    NbListModule,
    NbAccordionModule,
    NbButtonModule,
    FontAwesomeModule,
    SiteInitiativeConsumptionsChartComponent,
    NgStyle,
    InitiativeTrajectoryChartComponent,
    InitiativeCashFlowChartComponent,
    DatePipe,
    TranslateModule,
    UsernamePipe,
    CurrencyPipe,
    LightgalleryModule,
    InitiativeGainsListComponent,
    InitiativeTypePipe,
    TitleCasePipe
  ],
})
export class InitiativeSheetComponent implements OnInit, OnDestroy {

  @ViewChild('cellEditorDialog', { static: false })
  cellEditorDialog: TemplateRef<CellEditorDialogComponent>;

  @Input() public initiativeId: number;
  @Input() public minimizedView: boolean;

  private subscriptions: Subscription[] = [];
  public initiative: KPI.MappedInitiative;
  public initiativeLoading: boolean = true;
  public technicalStateLabel: string;
  public trueSavings: string = '';
  public scopeName: string;
  public formattedSavingsFullYear: number;
  public formattedRealSavings: number;
  public isPaybackVisible: boolean = true;
  public galleryId: number = UtilsService.makeRandomId();
  public budget: string = "";
  public docCount: number;
  public currency: string = '€';
  public seeMoreOpened: boolean = false;
  public readOnly: boolean = false;
  public customerConfig: KPI.CustomerConfig = null;

  /**
   * Image gallery settings
   */
  public settings: RequireAtLeastOne<LightGalleryCoreSettings> = {
    counter: false,
    speed: 500,
    plugins: [lgZoom, lgThumbnail],
    dynamic: true,
    dynamicEl: [],
    licenseKey: LIGHT_GALLERY_LICENSE_KEY,
    zoomFromOrigin: true,
  };

  private lightGallery!: LightGallery;

  public onInit = (detail): void => {
    this.lightGallery = detail.instance;
  };

  public openGallery = () => {
    this.lightGallery.openGallery();
  };

  public years = [];
  public canAccessAudit: boolean;

  constructor(
    private route: ActivatedRoute,
    private initiativesService: InitiativesService,
    private translate: TranslateService,
    private i18n: I18nService,
    private router: Router,
    private assetsService: AssetsService,
    private perimetersService: PerimetersService,
    private dialogService: NbDialogService,
    private initiativesChangesService: InitiativesChangesService,
    private cdRef: ChangeDetectorRef,
    private stateLevelService: StateLevelsService,
    private usersService: UsersService,
    private documentsService: DocumentsService,
    private chartFiltersService: ChartFiltersService,
    private chartsService: ChartsService,
    private config: ConfigService,
  ) { }

  ngOnInit() {
    this.currency = this.i18n.getCurrency();
    this.subscriptions.push(
      combineLatest([
        this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_ACCESS_AUDIT]),
        this.config.customerConfig$
      ]).subscribe(([accessAllowed, conf]) => {
        this.canAccessAudit = accessAllowed && conf.can_access_audit_app;
        this.customerConfig = conf;
      }),
      this.route.paramMap.pipe(
        filter((paramMap) => !!this.initiativeId || paramMap.has('id')),
        map((paramMap) => (this.initiativeId && this.minimizedView ? this.initiativeId : null) || paramMap.get('id')),
        switchMap((id) =>
          combineLatest([
            combineLatest([
              this.initiativesChangesService.pendingChangesNumber$.pipe(
                filter((pendingChangesNumber) => !pendingChangesNumber),
              ),
              this.chartsService._capexHasChanged$,
              this.initiativesService.refresh$
            ]).pipe(
              switchMap(() => this.initiativesService.getInitiative(Number(id))),
            ),
            this.perimetersService.perimeters$,
            this.usersService.currentUserPerimeterPermissions$,
          ])
        ),
      ).subscribe(([initiative, perimeters, permissions]) => {

        const site = initiative?.site;
        if (initiative && site && perimeters.find((p) => p.id === site.id)) {
          this.initiative = {
            ...initiative,
            budget_string: this.i18n.formatCurrency(initiative.budget),
          };

          this.initiativeLoading = false;
          const year = initiative.savings_start_at ? new Date(initiative.savings_start_at).getFullYear() : null;
          this.years = year ? R.range(year, year + 10) : [];

          if (initiative.assets && initiative.assets.length) {
            this.subscriptions.push(
              this.assetsService.getAssetsPictures(initiative.assets.map((a) => a.id)).subscribe((pictures) => {
                this.settings.dynamicEl = pictures.map((p) => ({
                  id: p.id,
                  src: p.picture,
                  thumb: p.thumbnail,
                }));

                this.cdRef.detectChanges();
              }),
            );
          }



          const relevantPerimeterPerm = permissions.find((perm) => perm.perimeter === this.initiative.site.id)
          this.readOnly = relevantPerimeterPerm?.read_only || !this.customerConfig?.is_show_edit

          if (this.initiative.investment) {
            this.budget = this.initiative.investment.slices.filter((s) =>
              s.status === this.initiative.investment.status
            ).sort((a, b) => a.year - b.year).map((s) =>
              this.i18n.formatCurrency(Number(s.price))
            ).join(', ');
          }

          if (this.initiative.emission_category?.scope) {
            this.scopeName = '(' + this.translate.instant('Scope') + ' ' + this.initiative.emission_category.scope.toString() + ')'
          }

          this.getAssetStateLabel();
          this.getDocumentsCount();
          this.cdRef.detectChanges();

          this.chartFiltersService.setFilter(
            'feasibilityConsumptionOrEmission',
            this.initiative.initiative_type?.gain_type === 'CARBON'
              ? KPI.FeasibilityConsumptionOrEmissionFilter.EMISSION
              : KPI.FeasibilityConsumptionOrEmissionFilter.CONSUMPTION,
          );
        } else {
          this.router.navigate(['energy', 'overview']);
        }
      }),

    );
  }

  /** Open an iframe to show the capex audit */
  public openInitiativeForm() {
    this.dialogService.open(
      InitiativeFormComponent,
      {
        autoFocus: false,
        closeOnBackdropClick: false,
        context: {
          initiative: this.initiative,
        },
      },
    )
  }

  public getPlannedAltScenarioNames(): String {
    return this.initiative?.instances?.filter((instance) =>
      instance.status_object
      && !instance.status_object.hypothesis
      && instance.scenario_object
      && !instance.scenario_object.is_main
    ).map((instance) => instance.scenario_object.name).join(', ');
  }

  public seeInvest(investmentId: number): void {
    this.dialogService.open(
      SheetDialogComponent,
      {
        autoFocus: false,
        context: { investmentId },
      },
    );
  }

  public getAssetStateLabel(): void {
    if (this.initiative.assets.length === 1) {
      this.subscriptions.push(
        this.assetsService.getAssetLifecycle(this.initiative.assets[0].id).subscribe((lifecycle) => {
          this.technicalStateLabel = this.stateLevelService.getTechnicalStateLabelFromNote(lifecycle.current_year_note);
          this.cdRef.detectChanges();
        }),
      )
    }
  }

  public seeAssets(assets): void {
    const context: Params = {
      autoFocus: false,
    }
    if (assets.length === 1) {
      context['assetId'] = assets[0].id
    } else {
      context['assetsList'] = true
      context['assetsListFilter'] = {
        asset_id: assets.map((asset) => asset.id.toString())
      }
    }
    this.dialogService.open(
      SheetDialogComponent,
      {
        context
      },
    );
  }

  private getDocumentsCount(): void {
    this.subscriptions.push(
      this.documentsService.getDocumentsCount(
        [],
        [],
        [this.initiative.id],
        []
      ).subscribe((docCount) => {
        this.docCount = docCount;
      })
    )
  }

  public seeDocuments(): void {
    this.dialogService.open(
      AgGridDocumentsComponent,
      {
        autoFocus: false,
        context: {
          title: this.translate.instant('the action'),
          assetIds: [],
          investIds: [],
          actionIds: [this.initiative.id]
        }
      }
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public hasSavings(): boolean {
    return this.getInitiativeSavings() > 0;
  }

  public getInitiativeSavings(): number {
    return this.initiative?.gains?.reduce((acc, gain) => acc + gain.theoretical_savings_full_year, 0) || 0;
  }

  public getUnitCost(): number {
    return this.initiative.budget / (this.initiative.quantity || 1);
  }

  public goToAudit(): void {
    this.initiativesService.openAuditInitiativeEditor({
      initiativeId: this.initiative.id,
      siteId: this.initiative.site.id,
    })
  }

}
